<template>
  <div style="display: flex; align-items: center">
    <h2 style="margin-right: 10px">Procesos</h2>
    <el-button
      type="primary"
      size="small"
      :icon="IconAdd"
      @click="addProcess"
      >Agregar proceso</el-button
    >
  </div>
  <el-table :data="processes">
    <el-table-column
      prop="Proceso"
      label="Proceso"
      width="200"
    >
      <template #default="scope">
        <custom-select
          v-model="scope.row.Proceso"
          :settings="{
            placeholder: 'Seleccionar proceso',
            options: procesoList,
            hideLabel: true,
          }"
          @update:modelValue="(val) => handleProcesoChange(scope.row, val)"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="Costo"
      label="Costo"
      width="200"
    >
      <template #default="scope">
        <custom-select
          v-model="scope.row.Costo"
          :settings="{
            placeholder: 'Seleccionar costo',
            options: costos,
            hideLabel: true,
          }"
          @update:modelValue="(val) => handleCostoChange(scope.row, val)"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="Velocidad"
      :label="`Velocidad (${unidadMedida}/${unidadTiempo})`"
      width="120"
    >
      <template #default="scope">
        <custom-input-number
          v-model="scope.row.Velocidad"
          :settings="{
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
          }"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="TiempoAjuste"
      label="Tiempo ajuste"
      width="120"
    >
      <template #default="scope">
        <custom-input-number
          v-model="scope.row.TiempoAjuste"
          :settings="{
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
          }"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="MermaFija"
      label="Merma fija"
      width="120"
    >
      <template #default="scope">
        <custom-input-number
          v-model="scope.row.MermaFija"
          :settings="{
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
          }"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="UnidadMedida"
      label="Unidad merma fija"
      width="160"
    >
      <template #default="scope">
        <custom-select
          v-model="scope.row.UnidadMedida"
          :settings="{
            placeholder: 'Sin unidad',
            options: opcionesUnidadMerma(scope.row),
            hideLabel: true,
            clearable: true,
          }"
          @update:modelValue="(val) => handleUnidadMermaChange(scope.row, val)"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="MermaPorcentaje"
      label="Merma porcentaje"
      width="120"
    >
      <template #default="scope">
        <custom-input-number
          v-model="scope.row.MermaPorcentaje"
          :settings="{
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
          }"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="Realimentacion"
      :label="`Realimentación (${unidadMedida})`"
      width="120"
    >
      <template #default="scope">
        <custom-input-number
          v-model="scope.row.Realimentacion"
          :settings="{
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
          }"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="TiempoRecarga"
      :label="`Tiempo recarga (Minutos)`"
      width="120"
    >
      <template #default="scope">
        <custom-input-number
          v-model="scope.row.TiempoRecarga"
          :settings="{
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
          }"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="UsaDesarrollo"
      label="Usa desarrollo"
      width="120"
      align="center"
    >
      <template #default="scope">
        <el-switch v-model="scope.row.UsaDesarrollo" />
      </template>
    </el-table-column>
    <el-table-column
      label="Acciones"
      width="150"
      align="center"
    >
      <template #default="scope">
        <el-button
          :icon="IconDelete"
          type="danger"
          @click.prevent="deleteRow(scope.row)"
        ></el-button>
      </template>
    </el-table-column>
  </el-table>
</template>

<script setup>
import { v4 as uuidv4 } from 'uuid';
import {
  onBeforeMount,
  computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import IconDelete from '~icons/mdi/delete';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import { useProcesoStore } from '@/stores/proceso_store.js';
import { useRecursoStore } from '@/stores/recurso_store.js';
import { useCostoStore } from '@/stores/costo/costo_store.js';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store.js';
import IconAdd from '~icons/mdi/add';
import proceso_recurso_model from '@/shared/models/proceso_recurso_model';

const procesoStore = useProcesoStore();
const { procesoList } = storeToRefs(procesoStore);

const costoStore = useCostoStore();
const { costos } = storeToRefs(costoStore);

const unidadMedidaStore = useUnidadMedidaStore();
const { unidadesMedida } = storeToRefs(unidadMedidaStore);

const recursoStore = useRecursoStore();
const { recurso } = storeToRefs(recursoStore);

const props = defineProps({
  processes: Array,
});

const emits = defineEmits(['deleteProcess', 'addProcess']);

const unidadMedida = computed(() => recurso.value.RecursoTipo?.UnidadMedida?.Descripcion || '');
const unidadTiempo = computed(() => recurso.value.RecursoTipo?.UnidadTiempo?.Descripcion || '');

const addProcess = () => {
  emits('addProcess', { ...JSON.parse(JSON.stringify(proceso_recurso_model)), id: uuidv4() });
};

const deleteRow = (row) => {
  emits('deleteProcess', row);
};

const handleProcesoChange = (row, selectedVal) => {
  row.Proceso = selectedVal;
  row.CodigoProceso = selectedVal ? selectedVal.Codigo : null;
};

const handleCostoChange = (row, selectedVal) => {
  row.Costo = selectedVal;
  row.CodigoCosto = selectedVal ? selectedVal.Codigo : null;
};

const handleUnidadMermaChange = (row, selectedVal) => {
  row.UnidadMedida = selectedVal || null;
  row.CodigoUnidadMedida = selectedVal?.Codigo ?? null;
};

// Solo se ofrecen unidades activas; si la unidad guardada se desactivó después,
// se agrega a las opciones de esa fila para que se muestre y no se borre al guardar.
const opcionesUnidadMerma = (row) => {
  const guardada = row.UnidadMedida;
  if (guardada?.Codigo && !unidadesMedida.value.some((u) => u.Codigo === guardada.Codigo)) {
    return [...unidadesMedida.value, guardada];
  }
  return unidadesMedida.value;
};

onBeforeMount(() => {
  unidadMedidaStore.emptyList();
  unidadMedidaStore.findAllUnidadMedidaHTTP({
    page: 0,
    Activo: true,
    sortBy: 'Descripcion',
    sort: 'Asc',
  });
  procesoStore.findAllProcesosHTTP({ page: 0 });
  costoStore.findAllCostosHTTP({ page: 0, sortBy: 'Descripcion', sort: 'asc' });
});
</script>
